import React from 'react';
import LazyLoad from 'react-lazyload';
import Head from 'next/head';
import imageUrlBuilder from '@sanity/image-url';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { ArrowForward, Check } from '@tmap/mmm-style-guide/src/Icon';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { useTheme } from '@tmap/mmm-style-guide/src/Theming';

import client from '../client';
import ColorBlocks from '../layouts/colorBlocks';
import Metatags from '../components/metatags';
import PressReferences from '../components/pressReferences';
import CardAd from '../components/cardAd';
import ByTheNumbersWidget from '../components/hiw/byTheNumbersWidget';
import FaqsBlock from '../components/hiw/faqsBlock';
import useRequireAuth from '../hooks/useRequireAuth';
import setCacheHeaders from '../lib/setCacheHeaders';
import CostOfLivingWidget from '../components/costOfLivingWidget';
import RecommendedContentBlock from '../components/homepage/recommendedContentBlock';

const builder = imageUrlBuilder(client);

const HeroWrapper = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '50%',
  paddingTop: theme.spacing(15),
  paddingBottom: theme.spacing(13),
  paddingRight: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    width: '100%',
    paddingRight: 0,
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
}));

const VideoWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '50%',
  left: '50%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    left: 0,
    width: '100%',
    zIndex: -1,
  },
  '& video': {
    height: '100%',
    width: 'auto',
    [theme.breakpoints.down('md')]: {
      height: '100%',
      width: '100%',
      objectFit: 'cover',
    },
  },
}));

const BorderedSection = styled(Box)(({ theme }) => ({
  borderWidth: '2px 0px 0px 0px',
  borderStyle: 'solid',
  borderColor: theme.palette.grey[200],
}));

const StepsMediaWrapper = styled(Box, {
  shouldForwardProp: (p) => p !== 'flush',
})(({ theme, flush }) => ({
  maxWidth: '100%',
  maxHeight: '75vw',
  width: '432px',
  height: '332px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
  '& .MuiCard-root': {
    background: flush ? 'transparent' : theme.palette.grey[200],
    borderRadius: '16px',
    padding: flush ? 0 : theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      padding: flush ? 0 : theme.spacing(2),
    },
  },
  '& .lazyload-wrapper': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  },
  '& .lazyload-placeholder': {
    width: '100%',
    height: '100%',
  },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    background: 'white',
  },
  '& video': {
    width: '100%',
    height: '100%',
    aspectRatio: '4/3',
    objectPosition: 'top',
    objectFit: 'cover',
  },
}));

const HeroCTAButton = styled(Button)(({ theme }) => ({
  button: {
    padding: theme.spacing(1, 6.875),
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    button: {
      width: '100%',
    },
  },
}));

const BenefitsButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(7),
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '42px',
  lineHeight: '117%',
  textAlign: 'center',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('md')]: {
    fontSize: '28px',
  },
}));

const SectionSubheader = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textAlign: 'center',
  fontWeight: 400,
  fontSize: '20px',
  marginBottom: theme.spacing(4.5),
  lineHeight: '160%',
  [theme.breakpoints.down('md')]: {
    fontSize: '16px',
  },
}));

const BenefitsContainerCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  padding: theme.spacing(2),
}));

const BenefitCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(2),
}));

const BenefitTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '16px',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
}));

const BenefitCheck = styled(Check)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.success.main,
}));

function InlineVideo(props) {
  return (
    <video
      muted
      loop
      autoPlay
      playsInline
      disablePictureInPicture
      disableRemotePlayback
      {...props}
    />
  );
}

function HowItWorksPage(props) {
  const {
    doc,
  } = props;

  const { isAuthenticated, requireAuthRedirect } = useRequireAuth();
  const handleSignUp = () => requireAuthRedirect();
  const theme = useTheme();

  return (
    <>
      <Metatags
        title='If you can work from anywhere, find the community of your dreams.'
        description={doc.descriptionCopyBody}
        canonical={`${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/how-it-works`}
        image={`${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/assets/hero-video-thumbnail.webp`}
      />
      <Head>
        <link rel='preload' as='image' href='assets/hero-video-thumbnail.webp' />
      </Head>
      <ColorBlocks>
        <Box position='relative'>
          <VideoWrapper>
            <InlineVideo src='assets/hero-video.mp4' poster='assets/hero-video-thumbnail.webp' />
          </VideoWrapper>
          <ColorBlocks.Block color='primaryDark' spacing='none' background={{ xs: 'rgba(31, 31, 31, 0.5)', md: theme.palette.primary.dark }}>
            <HeroWrapper>
              {doc.introCopy && (
                <Typography component='div' variant='overline' color='textSecondary' marginBottom={2}>
                  {doc.introCopy}
                </Typography>
              )}
              <Typography
                component='h1'
                fontWeight={800}
                color='textPrimary'
                lineHeight={1.2}
                marginBottom={7.125}
                sx={{ fontSize: { xs: '32px', md: '42px' }, maxWidth: { md: '438px' } }}
              >
                {doc.heroCopy}
              </Typography>
              {isAuthenticated ? (
                <HeroCTAButton href='/get-paid' variant='contained' color='convert' size='large' disableElevation>
                  {doc.heroProgramsButtonCopy || 'Browse Programs'}
                </HeroCTAButton>
              ) : (
                <HeroCTAButton variant='contained' color='convert' size='large' onClick={handleSignUp} disableElevation>
                  {doc.heroSignupButtonCopy || 'Sign Up and Explore'}
                </HeroCTAButton>
              )}
            </HeroWrapper>
          </ColorBlocks.Block>
        </Box>
        <ColorBlocks.Block>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <SectionHeader
                component='h3'
                sx={{ mt: 4.5 }}
              >
                {doc.summaryHeader}
              </SectionHeader>
            </Grid>
            <Grid item xs={12}>
              <SectionSubheader
                component='div'
                variant='body1'
              >
                {doc.summaryBody}
              </SectionSubheader>
            </Grid>
            <Grid item xs={12}>
              <LazyLoad height='66.5px' offset={600}>
                <PressReferences pressHits={doc.pressHits} condensed />
              </LazyLoad>
            </Grid>
          </Grid>
        </ColorBlocks.Block>
        <ColorBlocks.Block spacing={11.25} background='radial-gradient(49.04% 74.59% at 50% 50%, #97286B 0%, #691C4A 100%)'>
          <Box sx={{ maxWidth: '788px', margin: '0 auto' }}>
            <SectionHeader sx={{ color: theme.palette.primary.contrastText, mb: 5 }}>
              {doc.benefitsHeaderCopy || 'Access all of our benefits with a free MakeMyMove account.'}
            </SectionHeader>
            {isAuthenticated ? (
              <BenefitsButton
                href='/get-paid'
                variant='contained'
                color='convert'
                size='large'
                fullWidth
                disableElevation
              >
                {doc.benefitsBrowseButtonCopy || 'Browse Programs'}
              </BenefitsButton>
            ) : (
              <BenefitsButton
                variant='contained'
                color='convert'
                size='large'
                onClick={handleSignUp}
                fullWidth
                disableElevation
              >
                {doc.benefitsSignupButtonCopy || 'Sign Up and Get Started'}
              </BenefitsButton>
            )}
            <BenefitsContainerCard>
              <Grid container spacing={1}>
                {doc.mmmBenefits.map((benefit, i) => (
                  <Grid item xs={12} key={`benefit-${i}`}>
                    <BenefitCard elevation={0}>
                      <Grid container spacing={1}>
                        <Grid item>
                          <BenefitCheck />
                        </Grid>
                        <Grid item xs>
                          <BenefitTitle>
                            {benefit.leadCopy}
                          </BenefitTitle>
                          <Typography variant='body2' color='textSecondary'>
                            {benefit.subLeadCopy}
                          </Typography>
                        </Grid>
                      </Grid>
                    </BenefitCard>
                  </Grid>
                ))}
              </Grid>
            </BenefitsContainerCard>
          </Box>
        </ColorBlocks.Block>
        <ColorBlocks.Block spacing='small'>
          <Box sx={{ mt: 8.25 }}>
            <ByTheNumbersWidget stats={doc.byTheNumbers} />
          </Box>
        </ColorBlocks.Block>
        <ColorBlocks.Block spacing='small'>
          <SectionHeader sx={{ mt: 5.25, mb: 1 }}>
            {doc.hiwHeaderCopy}
          </SectionHeader>
          <SectionSubheader>
            {doc.hiwSubHeaderCopy}
          </SectionSubheader>
          <Grid container spacing={8}>
            {doc.hiwSteps.map((step, i) => (
              <Grid item xs={12} key={step._key}>
                <Grid container alignItems='center' spacing={{ xs: 2, md: 8 }} sx={{ flexDirection: { xs: 'row', md: i % 2 === 0 ? 'row' : 'row-reverse' } }}>
                  <Grid item xs={12} md={6}>
                    <Box display='flex' justifyContent='center'>
                      <StepsMediaWrapper flush={step.imageFlush}>
                        <LazyLoad offset={600}>
                          <Card elevation={0}>
                            {step.localVideo ? (
                              <InlineVideo
                                src={`assets/${step.localVideo}`}
                                poster={builder.image(step.image).format('webp').height(600).url()}
                              />
                            ) : (
                              <picture>
                                <source srcSet={builder.image(step.image).format('webp').height(400).url()} />
                                <img
                                  src={builder.image(step.image).format('webp').height(600).url()}
                                  alt={step.image?.alt}
                                />
                              </picture>
                            )}
                          </Card>
                        </LazyLoad>
                      </StepsMediaWrapper>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box padding={{ xs: 0, md: 8 }}>
                      <Typography variant='h5' fontWeight={700} mt={2} mb={1}>
                        {step.leadCopy}
                      </Typography>
                      <Typography variant='body1' color='textSecondary'>
                        {step.subLeadCopy}
                      </Typography>
                      {(step.linkUrl && step.linkText) && (
                        <Button href={step.linkUrl} color='secondary' endIcon={<ArrowForward />} sx={{ marginTop: 1, marginLeft: -0.5 }}>
                          {step.linkText}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </ColorBlocks.Block>
        <BorderedSection>
          <ColorBlocks.Block>
            <CostOfLivingWidget header='How far could your money go?' />
          </ColorBlocks.Block>
        </BorderedSection>
        <LazyLoad>
          <RecommendedContentBlock
            sinceDays={180}
            otherFilters={{ type: 'testimonial' }}
            header='Don’t just take our word for it...'
            description='Hear directly from Verified Movers about their experiences finding a new home with MakeMyMove.'
          />
        </LazyLoad>
        <ColorBlocks.Block>
          <FaqsBlock
            leadCopy={doc.faqLeadCopy}
            subLeadCopy={doc.faqSubLeadCopy}
            contactLink={doc.faqContactLink}
            faqs={doc.faqs}
          />
        </ColorBlocks.Block>
        {!isAuthenticated && (
          <ColorBlocks.Interstitial colorTop='light' colorBottom='light'>
            <CardAd
              leadCopy={doc.notificationBlockLead}
              subleadCopy={doc.notificationBlockSubLead}
            />
          </ColorBlocks.Interstitial>
        )}
      </ColorBlocks>
    </>
  );
}

export const getServerSideProps = async (context) => {
  const settings = await client.fetch(`
  *[_id == "howItWorksSettings"][0]{
    ...,
    faqs[]{
      _type == 'reference' => @->,
      _type != 'reference' => @,
    }
  }
  `);
  setCacheHeaders(context.res, 3600, 86400);
  return { props: { doc: settings } };
};

export default HowItWorksPage;
